/* eslint camelcase: 0 */
import isNil from 'lodash/isNil';
import round from 'lodash/round';
import throttle from 'lodash/throttle';
import {Component} from 'panel';

import {commaizeNumber} from 'common/util';
import {BarStyle, Bucket} from 'common/report/models/funnels/bar-chart/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import template from './index.jade';
import css from './index.styl?inline';

function formatBarWidth(segmentValue, maxValue) {
  return (segmentValue / round(parseFloat(maxValue), 4)) * 85 || 0;
}

interface ChartState {
  data: Bucket[];
  hoverTooltipActive: boolean;
}

/**
 * A horizontal bar-chart which calculates relative bar widths.
 *
 * @emits BarChart#clickBar when a bar is clicked
 */
export class BarChart extends Component<ChartState> {
  /**
   * @property {Bucket[]} data - array in which each entry is a dict representing a bucket
   *   example: data = [{
   *     label: `0 minutes - 15 minutes`,
   *     text: `No data in this bucket`,
   *   }, {
   *     label: `15 minutes - 30 minutes`,
   *     segments: [{
   *       color: `#333333`,
   *       count: 57,
   *       isClickable: true,
   *       isSelected: false,
   *       label: `Chrome`,
   *       name: `Chrome`,
   *       percentage: 0.3,
   *     }, {
   *       annotation: {
   *         value: `17m`,
   *       },
   *       color: `#999999`,
   *       count: 14,
   *       isClickable: true,
   *       isSelected: true,
   *       label: `Firefox`,
   *       name: `Firefox`,
   *       percentage: 0.5,
   *     },
   *     ...
   *     ],
   *   },
   *   ...
   *   ]
   * @property {boolean} disable-tooltip - hide the hover tooltips
   * @property {boolean} display-relative - show bar chart labels as
   *   percentages (relative) if true, else show them as numbers (absolute)
   */
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'disable-tooltip': `boolean`,
      'display-relative': `boolean`,
      'interval-title': {type: `string`, default: `Time Span`},
    };
  }

  get config() {
    return {
      defaultState: {
        data: [],
        hoverTooltipActive: false,
      },
      css,
      useShadowDom: true,
      template,
      helpers: {
        commaizeNumber,
        isNil,
        clickBar: (segment, rowLabel, rowIndex, queryIndex, ev) => {
          ev.stopPropagation();
          this.dispatchCustomEvent(`clickBar`, {segment, rowLabel, rowIndex, queryIndex, original: ev});
        },
        formatBarStyle: (segment) => {
          const width = this.attr(`display-relative`)
            ? formatBarWidth(segment.percentage, this.getMaxPercentage())
            : formatBarWidth(segment.count, this.getMaxCount());
          let style = {width: `${width}%`};

          if (segment.style === BarStyle.Stripe) {
            style = {
              ...style,
              ...{
                'background-image': `repeating-linear-gradient(-45deg, ${segment.color}, ${segment.color} 25%, #FFF 25%, #FFF 50%, ${segment.color} 50%)`,
                'background-size': `4px 4px`,
              },
            };
          } else {
            style = {
              ...style,
              ...{background: `${segment.color}`},
            };
          }

          return style;
        },
        formatAnnotationStyle: (segment) => {
          let style;
          switch (segment.style) {
            case BarStyle.Stripe:
              style = {
                'margin-top': `2px`,
                width: `10px`,
                height: `10px`,
                'border-radius': `2px`,
                'background-image': `repeating-linear-gradient(-45deg, ${segment.color}, ${segment.color} 25%, #FFF 25%, #FFF 50%, ${segment.color} 50%)`,
                'background-size': `4px 4px`,
              };
              break;
            case BarStyle.Solid:
              style = {
                'margin-top': `2px`,
                width: `10px`,
                height: `10px`,
                'border-radius': `2px`,
                background: `${segment.color}`,
              };
              break;
            default:
              style = {background: `${segment.color}`};
              break;
          }

          return style;
        },
        formatBarWidth: (segment) =>
          this.attr(`display-relative`)
            ? formatBarWidth(segment.percentage, this.getMaxPercentage())
            : formatBarWidth(segment.count, this.getMaxCount()),
        handleMouseMove: throttle(
          (bucket: Bucket, segmentIndex: number, isAnnotation: boolean, ev) => {
            if (!this.attr(`disable-tooltip`)) {
              this.dispatchCustomEvent(`hoverBar`, {
                isAnnotation,
                compose: true,
                bucket,
                segmentIndex,
                x: ev.clientX,
                y: ev.clientY,
              });
              this.update({hoverTooltipActive: true});
            }
          },
          50,
          {leading: true, trailing: false},
        ),
        handleMouseLeave: () => {
          this.dispatchCustomEvent(`offHoverBar`);
        },
      },
    };
  }

  getMaxCount(): number {
    const values = this.state.data
      .map(
        (row) =>
          row.segments && // So we want to include the second segment for compare to past. This is fragile :thinking:
          Math.max(row.segments[0].count, row.segments[1] && row.segments[1].style ? row.segments[1].count : 0),
      )
      .filter(Boolean);
    return Math.max(...values);
  }

  getMaxPercentage(): number {
    const values = this.state.data.flatMap((row) => row.segments && row.segments.map((segment) => segment.percentage));
    return Math.max(...values.filter(Boolean));
  }

  dispatchCustomEvent(eventName, detail = {}) {
    this.el.dispatchEvent(
      new CustomEvent(eventName, {
        composed: true,
        ...(detail && {detail}),
      }),
    );
  }

  set data(data) {
    this.update({data});
  }
}

export const FunnelsBarChartEl = defineAndMakeJsxFactory(`mp-funnels-bar-chart`, BarChart);
